import React, { useEffect, useState } from "react";
import { useTranslation, withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { request } from "../../../../../utils/Request";
import { isEmpty } from "lodash";
import { Sidebar } from "primereact/sidebar";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import VinWithType from "../../../../../components/Common/Display/VinWithType";

interface IFormProps {
    id: string | null;
    t: any | null;
    onRefresh?: any | null;
    onClose?: any | null;
    toastify?: any | null;
}

function AlpOutboundLoadGroupDetach(props: IFormProps) {
    const { t } = useTranslation();
    const { toastify } = props;
    const [panelState, setPanelState] = useState<boolean>(false);
    const [rows, setRows] = useState<any[]>([]);
    const [selectedRows, setSelectedRows] = useState<any[]>([]);
    const [loading, setLoading] = useState<boolean>(false);

    const onClose = () => {
        setPanelState(false);
        setRows([]);
        setSelectedRows([]);
        if ( props.onClose ) {
            props.onClose();
        }
    };

    const getDetail = async (groupToken: string) => {
        setLoading(true);
        try {
            const detailResponse = await request({
                url: `/suppliers/provider/aviso/outbound/groups/${ groupToken }`,
            });

            const detailData = detailResponse?.data?.data ?? detailResponse?.data ?? {};
            const loads = Array.isArray(detailData?.alp_outbound_loads) ? detailData.alp_outbound_loads : [];

            if ( loads.length <= 1 ) {
                toastify && toastify(t("action not allowed: 404"), "error");
                onClose();
                return;
            }

            const mappedRows = loads.map((load: any) => {
                const order = load?.alp_outbound_load_orders?.[0]?.order || {};
                return {
                    id: load?.id,
                    load_number: load?.load_number,
                    status: load?.status,
                    vin_number: order?.vin_number || order?.vin || "-",
                    dealer_name: load?.dealer_name || "-",
                    supplier_compound: load?.supplier_compound || "-",
                };
            }).filter((row: any) => !isEmpty(row.id));

            setRows(mappedRows);
            setSelectedRows([]);
            setPanelState(true);
        } catch (e: any) {
            if ( e.status === 404 ) {
                toastify && toastify(t("action not allowed: 404"), "error");
            } else {
                toastify && toastify(t("server error"), "error");
            }
            onClose();
        } finally {
            setLoading(false);
        }
    };

    const onSubmit = async () => {
        const selectedIds = (selectedRows || [])
            .map((row: any) => row?.id)
            .filter((id: any) => !isEmpty(id));

        if ( selectedIds.length <= 0 ) {
            toastify && toastify(t("no record selected"), "info");
            return;
        }

        if ( selectedIds.length >= rows.length ) {
            toastify && toastify(t("action not allowed: 404"), "error");
            return;
        }

        if ( isEmpty(props.id) ) {
            toastify && toastify(t("action not allowed: 404"), "error");
            return;
        }

        setLoading(true);
        try {
            await request({
                method: 'POST',
                url: `/suppliers/alp/aviso/outbound/loads/${ props.id }/detach`,
                data: {
                    load_ids: selectedIds,
                },
            });

            toastify && toastify(t("record updated", { item: t('outbound load') }), "info");
            onClose();
            if ( props.onRefresh ) {
                props.onRefresh();
            }
        } catch (e: any) {
            if ( e.status === 422 ) {
                toastify && toastify(t("unprocessable entity"), "error");
            } else if ( e.status === 404 ) {
                toastify && toastify(t("action not allowed: 404"), "error");
            } else {
                toastify && toastify(t("server error"), "error");
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if ( !isEmpty(props.id) ) {
            getDetail(String(props.id));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.id]);

    return (
        <Sidebar
            header={
                <div className="bg-body-tertiary card-header">
                    <h5 className={ "card-title mt-0 mb-1" }>{ t('detach from group') }</h5>
                    <div className="text-muted small">
                        { t("group token") }: <strong>{ props.id || "-" }</strong>
                    </div>
                </div>
            }
            visible={ panelState }
            position="right"
            onHide={ onClose }
            blockScroll={ true }
            closeOnEscape={ false }
            style={ { width: '1000px' } }
        >
            <div className="p-3">
                <div className="text-muted small mb-2">
                    { t("select loads to detach from group") }.
                </div>

                <DataTable
                    value={ rows }
                    dataKey="id"
                    selection={ selectedRows }
                    onSelectionChange={ (e: any) => setSelectedRows(e.value || []) }
                    scrollable
                    scrollHeight="620px"
                    stripedRows
                    showGridlines
                >
                    <Column selectionMode="multiple" headerStyle={ { width: '3rem' } } />
                    <Column field="load_number" header={ t('load number') } />
                    <Column
                        field="vin_number"
                        header={ t('vin number') }
                        body={ (row: any) => <VinWithType vinNumber={ row.vin_number } /> }
                    />
                    <Column field="status" header={ t('status') } />
                    <Column field="supplier_compound" header={ t('supplier compound') } />
                    <Column field="dealer_name" header={ t('dealer') } />
                </DataTable>
            </div>

            <div className="filter-card-footer" style={ { width: '985px' } }>
                <Button
                    type="button"
                    size={ "small" }
                    outlined
                    severity="secondary"
                    onClick={ onClose }
                    disabled={ loading }
                >
                    { t('cancel') }
                </Button>
                <Button
                    type="button"
                    className={ "ms-2" }
                    size={ "small" }
                    severity="warning"
                    onClick={ onSubmit }
                    disabled={ loading || (selectedRows || []).length <= 0 }
                >
                    <i className="fas fa-unlink me-2" />
                    { t('detach') }
                </Button>
            </div>
        </Sidebar>
    );
}

AlpOutboundLoadGroupDetach.propTypes = {
    t: PropTypes.any,
};

export default withTranslation()(AlpOutboundLoadGroupDetach);
